import React, {useState, useEffect, useRef} from 'react'

import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Button,
    Stack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    IconButton,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {convertDateToYYYYMMDD000000, formatDateToYYYYMMDD, generateGUID} from '../../utils/utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import RegisteredProductWidget from './registered-product/registered-product-widget'
import ProductRegistrationEditForm from './registered-product/registered-product-edit-form'
import RegisteredProductItem from './registered-product/registered-product-item'
import {CloseIcon} from '../../components/icons'
import {BRAND_SHORT_LABEL} from '../../brand'
import routingObject from '../../utils/static-routes'
import LoadingSpinner from '../../components/loading-spinner'
import useProductRegistrationHandler from '../../custom-hooks/useProductRegistrationHandler'
import {useTokenHelper} from '../../utils/token-helper'

const RegisteredProducts = ({customer, isGuest = false}) => {
    const [error, setError] = useState(null)
    const [products, setProducts] = useState([])
    const [isEditing, setIsEditing] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedProduct, setSelectedProduct] = useState(null)
    const styles = useMultiStyleConfig('Account')
    const appOrigin = useOrigin(false)
    const navigate = useNavigation()
    const [productRegistrationFailed, setProductRegistrationFailed] = useState(false)
    const handleProductRegistrationClick = useProductRegistrationHandler(routingObject)
    const {fetchSAPCPIValidToken} = useTokenHelper()

    const handleProductRetrieve = async () => {
        setIsLoading(true)
        setError(null)
        try {
            const accessToken = await fetchSAPCPIValidToken()
            // TODO dynamic CustomerId - Which customer ID should be used?
            //Pass the email in the query params to log it in case of gigyaId is missing
            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/retrieve-registered-products-sap-cpi?customerId=${customer.c_gigyaId}&customerEmail=${customer?.email}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                },
            )

            const result = await response.json()
            if (result.success) {
                setProductRegistrationFailed(false)
                const sortedProducts = result.products.sort((a, b) => {
                    return Date.parse(b.registrationDateKut) - Date.parse(a.registrationDateKut)
                })

                setProducts(sortedProducts)
                if (isGuest) {
                    setSelectedProduct(result.products?.[0])
                    setIsEditing(true)
                }
            } else if (result.error && result.error === 'NoGigya') {
                //Make this an Text and translated message
                setProductRegistrationFailed(true)
                setError('Please retry the product registration flow')
            } else {
                setError('Product retrieve failed: ' + result.error)
            }
        } catch (error) {
            setError('Error during product retrieve: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    // It will open an update form and at the submit call the confirmEdit() (push-product-registration-sap-cpi) endpoint with the updated product details
    const handleEditClick = (product) => {
        setSelectedProduct(product)
        setIsEditing(true)
    }

    const handleDeleteClick = (product) => {
        setSelectedProduct(product)
        setIsModalOpen(true)
    }

    const handleGoBack = () => {
        setIsEditing(false)
    }

    const handleCustomerSupportClick = () => {
        navigate('/contact-us')
    }

    const handleFAQsClick = () => {
        navigate(routingObject.FAQ_PATH)
    }

    const confirmEdit = async (product) => {
        if (!product) return
        try {
            const accessToken = await fetchSAPCPIValidToken()

            const updatedProductPayload = {
                ProductRegistrationForm: [
                    {
                        ProductRegistrationFormDetail: {
                            MessageID: generateGUID(),
                            Brand: BRAND_SHORT_LABEL,
                            RegistrationId: product.commerceRegistrationNumberKut,
                            SerialNumber: product.serialId,
                            IsAutomaticProductRegistration: false, // always false
                            SKU: product.skuKut,
                            Language: product.languagePrKut,
                            ProductLocationCountry: product.country,
                            Productname: product.productName,
                            Modelnumber: product.modelNumberKut,
                            //Primarycategory: product.primaryCategoryKut,
                            //Primarysubcategory: product.primarySubcategoryKut,
                            Source: product.source,
                            Purchasedate: convertDateToYYYYMMDD000000(product.purchaseData),
                            Registereddate: formatDateToYYYYMMDD(),
                            Registrationdeleted: false,
                            CustomerId: customer.c_gigyaId,
                            Receipts:
                                product?.receiptMime &&
                                product?.receiptName &&
                                product?.receiptBase64
                                    ? [
                                          {
                                              Receipt_1: {
                                                  ReceiptMime: product?.receiptMime,
                                                  ReceiptName: product?.receiptName,
                                                  Receipt: product?.receiptBase64,
                                              },
                                          },
                                      ]
                                    : [],
                        },
                    },
                ],
            }
            setIsLoading(true)
            if (productRegistrationFailed) {
                setError('Please retry the product registration flow')
                setIsLoading(false)
                return
            }
            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/push-product-registration-sap-cpi`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(updatedProductPayload),
                },
            )

            const result = await response.json()
            if (result.success) {
                // Update the product in the state
                setProducts(
                    products.map((p) => (p.serialId === selectedProduct.serialId ? product : p)),
                )
                setIsEditing(false)
                handleProductRetrieve()
            } else {
                setError('Product update failed: ' + result.error)
            }
        } catch (error) {
            setError('Error during product update: ' + error.message)
        } finally {
            setIsLoading(false)
        }
        setIsModalOpen(false)
    }

    const confirmDelete = async (selectedProduct) => {
        if (!selectedProduct) return
        setIsModalOpen(false)
        setIsLoading(true)
        try {
            const accessToken = await fetchSAPCPIValidToken()

            // TODO Eventually we could send more data; it needs to be clarified if any other parameters are needed
            const deleteProductPayload = {
                ProductRegistrationForm: [
                    {
                        ProductRegistrationFormDetail: {
                            RegistrationId: selectedProduct.commerceRegistrationNumberKut,
                            Registrationdeleted: true,
                            SKU: selectedProduct.skuKut,
                            CustomerId: customer.c_gigyaId,
                        },
                    },
                ],
            }
            if (productRegistrationFailed) {
                setError('Please retry the product registration flow')
                setIsLoading(false)
                return
            }
            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/push-product-registration-sap-cpi`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(deleteProductPayload),
                },
            )

            const result = await response.json()
            if (result.success) {
                // Remove the product from the state
                setProducts(
                    products.filter((product) => product.serialId !== selectedProduct.serialId),
                )
                if (isGuest) {
                    navigate('/')
                }
            } else {
                setError('Product delete failed: ' + result.error)
            }
        } catch (error) {
            setError('Error during product delete: ' + error.message)
        } finally {
            setIsLoading(false)
        }
        setIsModalOpen(false)
    }

    // Fetch registered products when the customer is available
    useEffect(() => {
        if (customer?.c_gigyaId) {
            handleProductRetrieve()
        }
    }, [customer?.c_gigyaId])

    const headingRef = useRef()
    useEffect(() => {
        // Focus the Title header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    return (
        <Stack
            data-testid="account-product-registration-page"
            position={'relative'}
            spacing={{base: 6, lg: 10}}
        >
            {!isEditing && (
                <Text as="h1" tabIndex="0" ref={headingRef} {...styles.headingTitle}>
                    <FormattedMessage
                        defaultMessage="Your registered products"
                        id="account.heading.registered_products"
                    />
                </Text>
            )}
            {isLoading && <LoadingSpinner />}
            {!isEditing && (
                <Box overflow={'hidden'}>
                    <Seo title="Registered Products" description="View registered products" />
                    <>
                        {/* {error && (
                            <Flex justify="center" mb={4}>
                                <Text color="red.500" fontWeight="bold">
                                    {error}
                                </Text>
                            </Flex>
                        )} */}
                        {/* <Flex>
                            <Text fontWeight="bold">Registered Products</Text>
                        </Flex> */}

                        <Flex direction="column" gap={{base: '16px', lg: '40px'}}>
                            {products &&
                                products.length > 0 &&
                                products.map((product, index) => (
                                    <RegisteredProductItem
                                        key={index}
                                        product={product}
                                        styles={styles}
                                        onEditClick={handleEditClick}
                                        onDeleteClick={handleDeleteClick}
                                        onCustomerSupportClick={handleCustomerSupportClick}
                                        onFAQsClick={handleFAQsClick}
                                    />
                                ))}

                            {products.length > 0 && !error && (
                                <Flex {...styles?.productRegButtonContainer}>
                                    <Button
                                        variant="primary"
                                        {...styles?.productRegButton}
                                        onClick={() => handleProductRegistrationClick()}
                                        data-testid="productRegistrationButton"
                                    >
                                        <Text>
                                            <FormattedMessage
                                                id="search.modal.productRegistration"
                                                defaultMessage="Product Registration"
                                            />
                                        </Text>
                                    </Button>
                                </Flex>
                            )}
                        </Flex>

                        {products.length === 0 && <RegisteredProductWidget />}
                    </>

                    <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} isCentered>
                        <ModalOverlay />
                        <ModalContent {...styles?.productRegModalContent}>
                            <ModalHeader {...styles?.productRegModalHeader}>
                                <Flex {...styles?.productRegMoldaHeaderContent}>
                                    <Text>
                                        <FormattedMessage
                                            id="product_registration.modal.confirm_request"
                                            defaultMessage="Confirm the request"
                                        />
                                    </Text>
                                    <IconButton
                                        icon={<CloseIcon {...styles.closeIcon} />}
                                        onClick={() => setIsModalOpen(false)}
                                        data-testid="close-icon"
                                    />
                                </Flex>
                            </ModalHeader>
                            <ModalBody {...styles?.productRegModalBody}>
                                <Text>
                                    <FormattedMessage
                                        id="product_registration.modal.description"
                                        defaultMessage="{serialId}{productName} will be removed from registered products"
                                        values={{
                                            serialId: selectedProduct?.commercialDescriptionKut,
                                            productName: selectedProduct?.productName,
                                        }}
                                    />
                                </Text>
                            </ModalBody>
                            <ModalFooter {...styles?.productRegModalFooter}>
                                <Flex {...styles?.productRegModalInnerFooter}>
                                    <Button
                                        variant="primary"
                                        width="full"
                                        onClick={() => confirmDelete(selectedProduct)}
                                        data-testid="confirm-button-modal"
                                    >
                                        <FormattedMessage
                                            id="product_registration.modal.confirm_button"
                                            defaultMessage="Confirm"
                                        />
                                    </Button>
                                    <Button
                                        variant="text-button"
                                        onClick={() => setIsModalOpen(false)}
                                    >
                                        <FormattedMessage
                                            id="product_registration.modal.cancel_button"
                                            defaultMessage="Cancel"
                                        />
                                    </Button>
                                </Flex>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>
                </Box>
            )}
            {isEditing && !isGuest && (
                <ProductRegistrationEditForm
                    goBack={handleGoBack}
                    confirmEdit={confirmEdit}
                    confirmDelete={confirmDelete}
                    product={selectedProduct}
                    isGuest={false}
                />
            )}
            {isEditing &&
                isGuest &&
                products?.map((product) => (
                    <ProductRegistrationEditForm
                        key={product?.objectId}
                        goBack={handleGoBack}
                        confirmEdit={confirmEdit}
                        confirmDelete={confirmDelete}
                        product={product}
                        isGuest={true}
                    />
                ))}
        </Stack>
    )
}

RegisteredProducts.getTemplateName = () => 'registered-products'

RegisteredProducts.propTypes = {
    customer: PropTypes.object,
    isGuest: PropTypes.bool,
}

export default RegisteredProducts
